import Link from 'next/link'
import { CheckCircle2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  ASSESSMENT_URL,
  BOOKING_URL,
} from '@/lib/attention-deficit-hyperactivity-disorder-therapy-data'
import type { LucideIcon } from 'lucide-react'
import type { ComponentProps } from 'react'

// ----------
// Section wrapper
// ----------
interface SectionProps {
  id?: string
  kicker?: string
  title: string
  children: React.ReactNode
}

const Section: React.FC<SectionProps> = ({ id, kicker, title, children }) => {
  return (
    <section id={id} className="py-12 sm:py-16 md:py-20">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl">
        {kicker && (
          <p className="mb-2 text-xs sm:text-sm font-medium tracking-wider uppercase text-emerald-600">
            {kicker}
          </p>
        )}
        <h2 className="text-xl sm:text-2xl md:text-4xl font-semibold tracking-tight mb-6 sm:mb-8">
          {title}
        </h2>
        {children}
      </div>
    </section>
  )
}

export { Section }

// ----------
// Pill - icon + label chip
// ----------
interface PillProps {
  icon?: LucideIcon
  children: React.ReactNode
}

const Pill: React.FC<PillProps> = ({ icon: Icon, children }) => {
  return (
    <span className="inline-flex items-center gap-1.5 sm:gap-2 rounded-full border border-border/60 bg-background px-2.5 sm:px-3 py-1 text-xs sm:text-sm">
      {Icon && <Icon className="h-3.5 w-3.5 sm:h-4 sm:w-4 shrink-0" />}
      {children}
    </span>
  )
}

export { Pill }

// ----------
// Bullet - checkmark list item
// ----------
interface BulletProps {
  children: React.ReactNode
}

const Bullet: React.FC<BulletProps> = ({ children }) => {
  return (
    <li className="flex gap-2 sm:gap-3 leading-relaxed text-sm">
      <CheckCircle2 className="mt-1 h-3.5 w-3.5 sm:h-4 sm:w-4 shrink-0 text-emerald-600" />
      {children}
    </li>
  )
}

export { Bullet }

// ----------
// AssessmentButton - reusable CTA for the free assessment
// ----------
type BtnSize = ComponentProps<typeof Button>['size']
type BtnVariant = ComponentProps<typeof Button>['variant']

interface AssessmentButtonProps {
  size?: BtnSize
  variant?: BtnVariant
}

const AssessmentButton: React.FC<AssessmentButtonProps> = ({
  size = 'lg',
  variant = 'secondary',
}) => {
  return (
    <Button asChild size={size} variant={variant}>
      <Link href={ASSESSMENT_URL}>Free ADHD self‑assessment</Link>
    </Button>
  )
}

export { AssessmentButton }

// ----------
// BookingButton - reusable CTA for booking
// ----------
interface BookingButtonProps {
  label?: string
  size?: BtnSize
  variant?: BtnVariant
}

const BookingButton: React.FC<BookingButtonProps> = ({
  label = 'Book now',
  size = 'lg',
  variant = 'default',
}) => {
  return (
    <Button asChild size={size} variant={variant}>
      <Link href={BOOKING_URL}>{label}</Link>
    </Button>
  )
}

export { BookingButton }

// ----------
// IconListCard - card with icon title + bullet list (used by impact, modalities, etc.)
// ----------
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'

interface IconListCardProps {
  title: string
  icon?: LucideIcon
  items: string[]
}

const IconListCard: React.FC<IconListCardProps> = ({
  title,
  icon: Icon,
  items,
}) => {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
          {Icon && <Icon className="h-4 w-4 sm:h-5 sm:w-5 shrink-0" />}
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <ul className="grid gap-1.5 sm:gap-2 text-xs sm:text-sm list-disc pl-4 sm:pl-5">
          {items.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      </CardContent>
    </Card>
  )
}

export { IconListCard }
